const FREE_PRIZE_DRAW_NO_PAYMENT_DISCLOSURE = "NO DONATION OR PAYMENT OF ANY KIND IS NECESSARY TO ENTER OR WIN. A PURCHASE, PAYMENT, OR DONATION WILL NOT INCREASE YOUR CHANCES OF WINNING.";
export const greatBritainFreePrizeDrawRulesTemplate = {
    fallbackValues: {
        sponsorLegalName: "the organisation running this Prize Draw",
        sponsorEntityType: "a nonprofit organisation",
        sponsorPrivacyPolicyReference: "contact the Promoter for details",
        excludedJurisdictions: null,
        entryOpenDatetime: "the date this Prize Draw is published",
        prizesTable: "The prize(s) offered in this Prize Draw are described on the campaign page.",
        prizeDeliveryTerms: "The Promoter will arrange delivery of the prize with the winner after verification is complete.",
        winnersListRequestDeadline: "thirty (30) days after the end of the Promotional Period",
        sweepstakesName: "this Prize Draw",
    },
    build: ({ text, paragraph, fillValues, zeffyPrivacyPolicyUrl }) => ({
        title: fillValues("{sponsorLegalName} – OFFICIAL FREE PRIZE DRAW TERMS AND CONDITIONS").toUpperCase(),
        disclosure: FREE_PRIZE_DRAW_NO_PAYMENT_DISCLOSURE,
        introduction: [
            {
                segments: [
                    { type: "strong", text: "How these Terms and Conditions work." },
                    text(' These are the Official Terms and Conditions for the Prize Draw run by {sponsorLegalName} (the "Promoter") on the Zeffy fundraising platform. Campaign-specific details, including the Promoter\'s identity, entry routes, opening and closing dates, prizes, and approximate values, are set out below. By participating in this Prize Draw, you agree to be bound by these Terms and Conditions.'),
                ],
            },
        ],
        sections: [
            {
                id: "promoter-and-platform",
                title: "1. The Promoter and the Platform",
                paragraphs: [
                    {
                        segments: [
                            { type: "strong", text: "The Promoter:" },
                            text(" {sponsorLegalName}, {sponsorEntityType}[[ (Registered Charity / Company No: {sponsorTaxId})]][[, with its registered address at {sponsorFullAddress}]]. The Promoter is solely responsible for the promotion, operation, and prize fulfilment of this Prize Draw."),
                        ],
                    },
                    {
                        segments: [
                            { type: "strong", text: "The Platform:" },
                            text(" Zeffy (operated by Simplyk, Inc.) provides the technology platform hosting the campaign. Zeffy is not the promoter or administrator of this Prize Draw, does not offer or award any prize, and accepts no liability in relation to the operation of the promotion."),
                        ],
                    },
                ],
            },
            {
                id: "eligibility",
                title: "2. Eligibility",
                paragraphs: [
                    paragraph("Open to legal residents of the United Kingdom (England, Scotland, Wales, and Northern Ireland) aged {minimumAge} years or over at the time of entry."),
                    paragraph("The Prize Draw is void outside the eligible territory and where prohibited by law.[[ Residents of {excludedJurisdictions} are excluded.]]"),
                    paragraph("Persons listed on the UK consolidated sanctions list (maintained by the Office of Financial Sanctions Implementation - OFSI) are not eligible to enter."),
                    paragraph("Employees, trustees, directors, or agents of the Promoter, Zeffy, or their immediate family members (spouse, parent, child, sibling) or household members are not eligible to participate."),
                    paragraph("Entries must be submitted by individuals on their own behalf. Entries submitted via automated systems, bulk syndicates, or commercial entry services will be disqualified."),
                ],
            },
            {
                id: "promotional-period",
                title: "3. Promotional Period",
                paragraphs: [
                    paragraph('The Prize Draw opens at {entryOpenDatetime} ({timezoneLabel}) and closes at {entryCloseDatetime} ({timezoneLabel}) (the "Promotional Period").'),
                    paragraph("Entries received after the closing date and time will not be accepted. The Promoter’s official clock will determine the timing of entries."),
                ],
            },
            {
                id: "how-to-enter",
                title: "4. How to Enter",
                paragraphs: [
                    paragraph("There are two routes of entry. Both routes have the exact same chance of winning per entry:"),
                    {
                        segments: [
                            { type: "strong", text: "Route A – Online Donation Entry:" },
                            text(" Visit {campaignUrl} during the Promotional Period, select your donation level, enter your contact details (first name, last name, email address, phone number, and postal address), and submit payment. Entries are allocated as follows:"),
                        ],
                    },
                    paragraph("{entryLevelsTable}"),
                    paragraph("Maximum entries per person via donation: {maxPaidEntriesPerPerson}. If a payment is subsequently cancelled, refunded, or charged back, associated entries will be voided."),
                    {
                        segments: [
                            { type: "strong", text: "Route B – Free Entry Route (No Purchase Necessary):" },
                            text(" Visit the free entry form directly via this link: "),
                            { type: "freeEntryLink", text: "Free Entry Form" },
                            text(". Complete the required contact details (first name, last name, email address, phone number, and postal address). Each valid submission via the free entry route grants {freeEntriesPerRequest} entry/entries. The maximum limit is {freeEntryLimit}."),
                        ],
                    },
                    paragraph("Entry Integrity: Entrants must provide accurate personal details. Entries containing false or misleading information will be disqualified. The Promoter accepts no responsibility for network, technical, or postal failures resulting in lost, delayed, or unrecorded entries."),
                ],
            },
            {
                id: "donations-and-tax",
                title: "5. Donations and Tax",
                paragraphs: [
                    {
                        segments: [
                            text("Donations made to obtain entries into this Prize Draw provide a direct chance to win a prize. Under HM Revenue & Customs (HMRC) rules, payments or donations made to participate in a prize draw do not qualify for "),
                            { type: "strong", text: "Gift Aid" },
                            text(", and no Gift Aid tax reclaim will be made on entry amounts. In the UK, prize awards are generally free of UK income tax and capital gains tax for the recipient. Entrants are responsible for any personal tax advice regarding their circumstances."),
                        ],
                    },
                ],
            },
            {
                id: "odds-and-selection",
                title: "6. Winner Selection and Odds",
                paragraphs: [
                    {
                        segments: [
                            { type: "strong", text: "Odds:" },
                            text(" The odds of winning depend on the total number of eligible entries received across both entry routes."),
                        ],
                    },
                    {
                        segments: [
                            { type: "strong", text: "Selection:" },
                            text(" On or about {drawingDatetime}, the potential winner(s) will be selected at random from all eligible entries received, using an independently verified computerised random draw process ({drawingMethod})."),
                        ],
                    },
                    paragraph("{numberOfPrizes} prize(s) will be awarded in total. If multiple prizes are offered, entries are drawn sequentially as described in Section 7. Once an entry is drawn, it is removed from the pool; no single entry may win more than one prize."),
                ],
            },
            {
                id: "prizes",
                title: "7. Prizes",
                paragraphs: [
                    paragraph("The prizes available to be won are:"),
                    paragraph("{prizesTable}"),
                    paragraph("Cash option: {cashOptionStatement}"),
                    paragraph("Prizes are non-transferable, non-negotiable, and cannot be exchanged for cash (unless a cash alternative is expressly specified above). The Promoter reserves the right to substitute any prize with an alternative prize of equal or greater value should circumstances beyond its reasonable control make it necessary to do so. Nothing in these terms excludes statutory consumer rights under the Consumer Rights Act 2015."),
                ],
            },
            {
                id: "winner-notification",
                title: "8. Winner Notification and Claiming the Prize",
                paragraphs: [
                    paragraph("Winners will be notified via {winnerNotificationMethod} on or about {winnerNotificationDate} using the contact details provided upon entry."),
                    paragraph("Winners must respond within {winnerResponseDeadlineDays} days of notification to confirm acceptance. If a winner cannot be contacted, fails to respond within the designated timeframe, is found to be ineligible, or declines the prize, the Promoter reserves the right to award the prize to a reserve winner selected at random from the remaining eligible entries."),
                    paragraph("Delivery terms: {prizeDeliveryTerms}"),
                ],
            },
            {
                id: "publicity",
                title: "9. Winner Publication and Publicity",
                paragraphs: [
                    paragraph("In accordance with the UK Advertising Standards Authority (CAP Code Rule 8.28.5), the Promoter must either publish or make available information confirming that a valid award took place. To comply, the Promoter will publish or make available the surname and county of the major prize winner(s)."),
                    {
                        segments: [
                            { type: "strong", text: "Right to Object:" },
                            text(" Winners have the right to object to their surname and county being published or made available to the public. If you object, please inform the Promoter upon notification. In such cases, the Promoter must still provide the full details and winning entry to the Advertising Standards Authority (ASA) upon request to demonstrate that a valid draw took place."),
                        ],
                    },
                    paragraph("The Promoter will not use a winner’s photograph or likeness for marketing or advertising without the winner’s prior explicit consent."),
                ],
            },
            {
                id: "privacy",
                title: "10. Data Protection",
                paragraphs: [
                    {
                        segments: [
                            text("Personal data collected in connection with this Prize Draw will be processed in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. Data will only be used to administer the Prize Draw, verify eligibility, contact winners, and fulfil prizes. For further information, see the Promoter's Privacy Policy: {sponsorPrivacyPolicyReference} and "),
                            { type: "externalLink", text: "Zeffy’s Privacy Policy", href: zeffyPrivacyPolicyUrl },
                            text("."),
                        ],
                    },
                ],
            },
            {
                id: "release-and-liability",
                title: "11. Limitation of Liability",
                paragraphs: [
                    paragraph("Nothing in these Terms and Conditions limits or excludes the Promoter’s or Zeffy’s liability for:\n1. Death or personal injury caused by its negligence;\n2. Fraud or fraudulent misrepresentation; or\n3. Any other liability that cannot be excluded or limited by the laws of England and Wales."),
                    paragraph("Subject to the above, neither the Promoter nor Zeffy shall be liable for any indirect, special, or consequential loss, or for any technical failure, network outage, or software error that impedes participation or winner notification."),
                ],
            },
            {
                id: "general-conditions",
                title: "12. General Conditions",
                paragraphs: [
                    paragraph("The Promoter reserves the right to suspend, cancel, or amend the Prize Draw only where it becomes unavoidable due to circumstances outside its reasonable control, acting in accordance with the UK CAP Code."),
                    paragraph("Any attempt to deliberately damage the platform or undermine the legitimate operation of this draw may result in disqualification and civil or criminal proceedings."),
                ],
            },
            {
                id: "governing-law",
                title: "13. Governing Law and Jurisdiction",
                paragraphs: [
                    paragraph("These Terms and Conditions are governed by English law. Any dispute or claim arising out of or in connection with them will be subject to the non-exclusive jurisdiction of the courts of England and Wales. (If you are resident in Scotland or Northern Ireland, you may also bring proceedings in your local court)."),
                ],
            },
            {
                id: "winners-list",
                title: "14. Winners List Requests",
                paragraphs: [
                    paragraph('To obtain details of the winner (surname and county), please send a request to {sponsorContactEmail} or by post to {sponsorLegalName}[[, {sponsorFullAddress}]], referencing "{sweepstakesName} - Winners List", no later than {winnersListRequestDeadline}.'),
                ],
            },
            {
                id: "contact",
                title: "15. Contact",
                paragraphs: [
                    paragraph("For questions regarding this Prize Draw, contact the Promoter at {sponsorContactEmail}[[, or by phone at {sponsorContactPhone}]]."),
                ],
            },
        ],
    }),
};
